
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Calculator, Euro, Clock, Info, AlertTriangle, CheckCircle, ArrowRight, Calendar } from "lucide-react";
import { format, isWeekend, addDays, isSameDay } from "date-fns";
import { de } from "date-fns/locale";
import { checkAvailability, fetchBookingPrice } from "@/utils/api";
import { useBookingSubmission } from "@/hooks/useBookingApi";
import { validateBookingTime, isWeekendTime, isDateBookable } from "@/utils/validation";

/**
 * Buchungsformular für Veranstaltungsraum
 * Ermöglicht die Eingabe aller notwendigen Daten für eine Buchungsanfrage
 * Berechnet automatisch den Preis basierend auf Zeitraum und Optionen
 * 
 * @param {Object} props - Komponenten-Props
 * @param {Object} props.selectedDateRange - Ausgewählter Datumsbereich mit start und end
 * @param {Function} props.onBookingComplete - Callback für abgeschlossene Buchungen
 * @param {Array} props.availabilityData - Verfügbarkeitsdaten für Überlappungsprüfung
 * @param {boolean} props.compact - Ob das Formular kompakt angezeigt werden soll
 */
export default function BookingForm({ selectedDateRange, onBookingComplete, availabilityData = [], compact = false }) {
  // Aktueller Formularschritt
  const [currentStep, setCurrentStep] = useState(1);
  
  
  /**
   * Formular-Daten für alle Eingabefelder
   * Wird bei jeder Änderung aktualisiert und für die Preisberechnung verwendet
   */
  const [formData, setFormData] = useState({
    start_time: "08:00",  // Angepasst an neue Mindestzeit
    end_time: "22:00",    // Angepasst an neue Maximalzeit unter der Woche
    is_local_club: false,
    needs_dishes: false,
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_organization: "",  // Neues Feld für Unternehmen/Verein
    customer_tax_id: "",        // Neues Feld für Steuernummer
    customer_street: "",
    customer_zip: "",
    customer_city: "",
    event_purpose: "",
    notes: "",
    confirmed_terms: false
  });

  // State für die Preisberechnung, Validierung und den Submit-Status
  const [priceCalculation, setPriceCalculation] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [availabilityCheck, setAvailabilityCheck] = useState(null);
  
  // Verwende den neuen Booking-Submission Hook
  const { submitBooking, loading: isSubmitting, error: submissionError, fieldErrors: backendFieldErrors, success: submitSuccess, resetState } = useBookingSubmission();

  /**
   * Generiert Zeitoptionen in halben Stunden
   * Startzeit: 8:00-22:00 (alle Tage)
   * Endzeit: Wochentag bis 22:00, Wochenende komplett frei (0:00-23:30)
   * @param {boolean} isEndTime - Ob es sich um Endzeit-Optionen handelt
   * @returns {Array} Array von Zeitstrings im Format "HH:MM"
   */
  const generateTimeOptions = (isEndTime = false) => {
    const times = [];
    
    if (isEndTime && selectedDateRange) {
      const endDay = selectedDateRange.end.getDay();
      const isEndWeekday = endDay >= 1 && endDay <= 4; // Mo-Do
      const isEndWeekend = endDay === 0 || endDay === 5 || endDay === 6; // Fr-So
      
      if (isEndWeekend) {
        // Wochenende: Komplett frei von 0:00 bis 23:30
        for (let hour = 0; hour <= 23; hour++) {
          for (let minute of [0, 30]) {
            const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
            times.push(time);
          }
        }
      } else {
        // Wochentag: 8:00-22:00
        for (let hour = 8; hour <= 22; hour++) {
          for (let minute of [0, 30]) {
            if (hour === 22 && minute === 30) break; // Bis 22:00
            const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
            times.push(time);
          }
        }
      }
    } else {
      // Startzeit: Immer 8:00-22:00
      for (let hour = 8; hour <= 22; hour++) {
        for (let minute of [0, 30]) {
          if (hour === 22 && minute === 30) break; // Bis 22:00
          const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          times.push(time);
        }
      }
    }
    
    return times;
  };

  // Zeitoptionen werden dynamisch basierend auf selectedDateRange generiert
  const startTimeOptions = generateTimeOptions(false);
  const endTimeOptions = generateTimeOptions(true);

  // Vorlauf-Prüfung für ausgewählten Zeitraum (min. 3 Tage / max. 1 Jahr)
  const dateBookableCheck = selectedDateRange
    ? isDateBookable(selectedDateRange.start, selectedDateRange.end)
    : { isBookable: true, reason: null, code: null };
  const isDateRangeBookable = dateBookableCheck.isBookable;
  
  // Aktualisiere Endzeit wenn sie durch neue Zeitbeschränkungen ungültig wird
  useEffect(() => {
    if (selectedDateRange && formData.end_time) {
      const validEndTimes = generateTimeOptions(true);
      if (!validEndTimes.includes(formData.end_time)) {
        // Wenn die aktuelle Endzeit nicht mehr gültig ist, setze sie auf die letzte verfügbare Zeit
        const lastValidTime = validEndTimes[validEndTimes.length - 1];
        if (lastValidTime) {
          handleInputChange("end_time", lastValidTime);
        }
      }
    }
  }, [selectedDateRange]);

  /**
   * Validiert die Eingaben und prüft Verfügbarkeit
   * @returns {Object} Validierungsergebnis mit isValid und errors
   */
  const validateForm = () => {
    const errors = {};
    
    // Zeitvalidierung mit der neuen Utility
    const timeValidation = validateBookingTime({
      startTime: formData.start_time,
      endTime: formData.end_time,
      startDate: selectedDateRange.start,
      endDate: selectedDateRange.end
    });
    
    if (!timeValidation.isValid) {
      errors.time = timeValidation.errors.join(', ');
    }
    
    // Vorlauf-Regel prüfen (min. 3 Tage / max. 1 Jahr)
    if (selectedDateRange) {
      const bookableCheck = isDateBookable(selectedDateRange.start, selectedDateRange.end);
      if (!bookableCheck.isBookable) {
        errors.advance_booking = bookableCheck.reason;
      }
    }
    
    // Verfügbarkeitsprüfung - nur bei accepted (booked) Konflikten blockieren
    if (availabilityCheck && !availabilityCheck.isAvailable) {
      if (availabilityCheck.hasAccepted) {
        errors.availability = "Dieser Zeitraum ist bereits belegt";
      }
      // Bei requested (pending) Konflikten keinen Fehler setzen - nur Warnung anzeigen
    }
    
    // Pflichtfelder
    if (!formData.customer_name.trim()) errors.customer_name = "Name ist erforderlich";
    if (!formData.customer_email.trim()) errors.customer_email = "E-Mail ist erforderlich";
    if (!formData.customer_phone.trim()) errors.customer_phone = "Telefon ist erforderlich";
    if (!formData.customer_street.trim()) errors.customer_street = "Straße ist erforderlich";
    if (!formData.customer_zip.trim()) errors.customer_zip = "PLZ ist erforderlich";
    if (!formData.customer_city.trim()) errors.customer_city = "Ort ist erforderlich";
    if (!formData.event_purpose.trim()) errors.event_purpose = "Zweck ist erforderlich";
    if (!formData.confirmed_terms) errors.confirmed_terms = "Bitte bestätigen Sie die Gebührenordnung";
    
    // Validierung für optionale Felder
    if (formData.customer_organization.trim() && formData.customer_organization.length > 255) {
      errors.customer_organization = "Unternehmen/Verein darf maximal 255 Zeichen haben";
    }
    
    if (formData.customer_tax_id.trim()) {
      const taxIdRegex = /^[A-Z]{2}[0-9]{9}$/;
      if (!taxIdRegex.test(formData.customer_tax_id)) {
        errors.customer_tax_id = "Steuernummer muss im Format DE123456789 sein";
      }
      if (formData.customer_tax_id.length > 50) {
        errors.customer_tax_id = "Steuernummer darf maximal 50 Zeichen haben";
      }
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  };

  /**
   * Preisberechnung basierend auf ausgewähltem Zeitraum und Optionen
   * Wird automatisch ausgeführt wenn sich relevante Werte ändern
   * Verwendet die neue Backend-API für Preisberechnung
   */
  useEffect(() => {
    if (selectedDateRange && formData.start_time && formData.end_time) {
      const fetchPrice = async () => {
        setPriceLoading(true);
        setPriceError(null);
        
        try {
          const calculation = await fetchBookingPrice({
            startDate: selectedDateRange.start,
            endDate: selectedDateRange.end,
            startTime: formData.start_time,
            endTime: formData.end_time,
            isLocalClub: formData.is_local_club,
            needsDishes: formData.needs_dishes
          });

          setPriceCalculation(calculation);
        } catch (error) {
          console.error('Fehler beim Abrufen des Preises:', error);
          setPriceError(error.message);
          // Fallback: Setze einen leeren Preis
          setPriceCalculation({
            totalPrice: 0,
            details: ['Preis konnte nicht berechnet werden'],
            currency: 'EUR'
          });
        } finally {
          setPriceLoading(false);
        }
      };

      fetchPrice();
    }
  }, [selectedDateRange, formData.start_time, formData.end_time, formData.is_local_club, formData.needs_dishes]);

  /**
   * Prüft die Verfügbarkeit des ausgewählten Zeitraums (nur Datum)
   */
  useEffect(() => {
    if (selectedDateRange && availabilityData && availabilityData.length > 0) {
      // Prüfe Verfügbarkeit für den ganzen Tag (00:00-23:59)
      const startDateTime = new Date(selectedDateRange.start);
      const endDateTime = new Date(selectedDateRange.end);
      
      // Setze Zeiten auf ganzen Tag
      startDateTime.setHours(0, 0, 0, 0);
      endDateTime.setHours(23, 59, 59, 999);
      
      // Überlappungsprüfung
      const availability = checkAvailability(startDateTime, endDateTime, availabilityData);
      setAvailabilityCheck(availability);
    }
  }, [selectedDateRange, availabilityData]);

  /**
   * Prüft die Verfügbarkeit des ausgewählten Zeitraums (mit spezifischen Zeiten)
   */
  useEffect(() => {
    if (selectedDateRange && formData.start_time && formData.end_time && availabilityData && availabilityData.length > 0) {
      const startDateTime = new Date(selectedDateRange.start);
      const endDateTime = new Date(selectedDateRange.end);
      
      // Setze Zeiten
      const [startHour, startMinute] = formData.start_time.split(':').map(Number);
      const [endHour, endMinute] = formData.end_time.split(':').map(Number);
      
      startDateTime.setHours(startHour, startMinute, 0, 0);
      endDateTime.setHours(endHour, endMinute, 0, 0);
      
      // Überlappungsprüfung
      const availability = checkAvailability(startDateTime, endDateTime, availabilityData);
      setAvailabilityCheck(availability);
    }
  }, [selectedDateRange, formData.start_time, formData.end_time, availabilityData]);

  /**
   * Aktualisiert die Verfügbarkeitsprüfung wenn availabilityData geladen werden
   */
  useEffect(() => {
    if (selectedDateRange && availabilityData && availabilityData.length > 0) {
      // Prüfe Verfügbarkeit für den ganzen Tag (00:00-23:59)
      const startDateTime = new Date(selectedDateRange.start);
      const endDateTime = new Date(selectedDateRange.end);
      
      // Setze Zeiten auf ganzen Tag
      startDateTime.setHours(0, 0, 0, 0);
      endDateTime.setHours(23, 59, 59, 999);
      
      // Überlappungsprüfung
      const availability = checkAvailability(startDateTime, endDateTime, availabilityData);
      setAvailabilityCheck(availability);
    }
  }, [availabilityData]);

  /**
   * Behandelt das Absenden des Formulars
   * Validiert die Daten und ruft den onBookingComplete-Callback auf
   * @param {Event} e - Submit-Event des Formulars
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateForm();
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    // Verwende den neuen Booking-Submission Hook
    const result = await submitBooking(formData, selectedDateRange, priceCalculation.totalPrice);
    
    if (result.success) {
      // Callback mit API-Response und Formulardaten für Anzeige
      onBookingComplete(result.bookingResponse, result.originalFormData);
    } else {
      // Wenn Backend-Feld-Fehler vorhanden sind, wechsle zu Schritt 2 (Persönliche Daten)
      if (result.fieldErrors && Object.keys(result.fieldErrors).length > 0) {
        setCurrentStep(2);
      }
      
      // Kombiniere Backend-Feld-Fehler mit allgemeinem Submit-Fehler
      const combinedErrors = {
        ...result.fieldErrors, // Backend-Validierungsfehler pro Feld
        submit: result.error // Allgemeine Fehlermeldung
      };
      setValidationErrors(combinedErrors);
      
      // Scrolle zum ersten Fehlerfeld nach kurzer Verzögerung (damit Schritt-Wechsel abgeschlossen ist)
      setTimeout(() => {
        const firstErrorField = Object.keys(result.fieldErrors)[0];
        if (firstErrorField) {
          const errorElement = document.getElementById(firstErrorField);
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            errorElement.focus();
          }
        } else if (result.error) {
          // Falls kein spezifisches Feld, scrolle zur Submit-Fehlermeldung
          const submitErrorElement = document.querySelector('[id*="submit"]') || document.querySelector('.text-red-600');
          if (submitErrorElement) {
            submitErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 100);
    }
  };

  /**
   * Behandelt Änderungen an Formularfeldern
   * Aktualisiert den formData-State und löst die Preisberechnung aus
   * @param {string} field - Feldname der geändert wurde
   * @param {any} value - Neuer Wert des Feldes
   */
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Entferne Validierungsfehler für das geänderte Feld
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  /**
   * Wechselt zum nächsten Formularschritt
   */
  const nextStep = () => {
    if (currentStep === 1) {
      // Validiere Vorlauf-Regel (min. 3 Tage / max. 1 Jahr)
      const bookableCheck = isDateBookable(selectedDateRange.start, selectedDateRange.end);
      if (!bookableCheck.isBookable) {
        setValidationErrors({ advance_booking: bookableCheck.reason });
        return;
      }

      // Validiere Schritt 1
      const timeValidation = validateBookingTime({
        startTime: formData.start_time,
        endTime: formData.end_time,
        startDate: selectedDateRange.start,
        endDate: selectedDateRange.end
      });
      
      if (timeValidation.isValid) {
        setCurrentStep(2);
        setValidationErrors({});
      } else {
        setValidationErrors({ time: timeValidation.errors.join(', ') });
      }
    }
  };

  /**
   * Wechselt zum vorherigen Formularschritt
   */
  const prevStep = () => {
    setCurrentStep(currentStep - 1);
    setValidationErrors({});
  };

  return (
    <div>
      {/* Live-Region für Screen Reader */}
      <div 
        id="booking-form-announcements" 
        aria-live="polite" 
        aria-atomic="true"
        className="sr-only"
      >
        {validationErrors.submit && "Formularfehler: " + validationErrors.submit}
        {submissionError && "Formularfehler: " + submissionError}
        {isSubmitting && "Formular wird gesendet..."}
        {submitSuccess && "Buchungsanfrage erfolgreich gesendet!"}
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">

      {/* Schritt 1: Zeitauswahl und Optionen */}
      {currentStep === 1 && (
        <div className="space-y-4">
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-2">Schritt 1: Zeitauswahl und Optionen</h3>
          </div>

          {/* Zeitauswahl */}
          <div className="space-y-3">
            <div>
              <Label htmlFor="start_time" className="text-sm font-medium">Startzeit *</Label>
              <Select 
                value={formData.start_time} 
                onValueChange={(value) => handleInputChange("start_time", value)}
              >
                <SelectTrigger 
                  className="h-9"
                  aria-describedby="start_time_error"
                  aria-invalid={!!validationErrors.start_time}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {startTimeOptions.map(time => (
                    <SelectItem key={time} value={time}>{time} Uhr</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {validationErrors.start_time && (
                <div id="start_time_error" className="text-red-600 text-sm mt-1" role="alert">
                  {validationErrors.start_time}
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="end_time" className="text-sm font-medium">Endzeit *</Label>
              <Select 
                value={formData.end_time} 
                onValueChange={(value) => handleInputChange("end_time", value)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {endTimeOptions
                    .filter(time => {
                      // Bei gleichem Datum: Endzeit muss nach Startzeit liegen
                      if (selectedDateRange.start.getTime() === selectedDateRange.end.getTime()) {
                        return time > formData.start_time;
                      }
                      // Bei unterschiedlichen Daten: alle Zeiten erlaubt
                      return true;
                    })
                    .map(time => (
                      <SelectItem key={time} value={time}>{time} Uhr</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Optimierungshinweis */}
          {priceCalculation && priceCalculation.optimizedEndTime && !isWeekendTime(selectedDateRange.end, formData.end_time) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-blue-800 text-sm">
                <Info className="w-4 h-4" />
                <span>
                  <strong>Optimierung:</strong> Zum gleichen Preis ist eine Buchung bis {priceCalculation.optimizedEndTime} Uhr möglich.
                </span>
              </div>
            </div>
          )}

          {/* Zeitvalidierungsfehler */}
          {validationErrors.time && (
            <div className="text-red-600 text-xs flex items-center gap-2">
              <AlertTriangle className="w-3 h-3" />
              {validationErrors.time}
            </div>
          )}

          {/* Vorlauf-Regel Fehler */}
          {(validationErrors.advance_booking || !isDateRangeBookable) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-red-800 text-sm">
                <AlertTriangle className="w-4 h-4" />
                <span>{validationErrors.advance_booking || dateBookableCheck.reason}</span>
              </div>
            </div>
          )}

          {/* Zusatzoptionen */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="is_local_club"
                checked={formData.is_local_club}
                onCheckedChange={(checked) => handleInputChange("is_local_club", checked)}
              />
              <Label htmlFor="is_local_club" className="text-sm">Lokaler Verein (ermäßigte Preise am Wochenende: Fr-So)</Label>
            </div>

            {/* <div className="flex items-center space-x-2">
              <Checkbox 
                id="needs_dishes"
                checked={formData.needs_dishes}
                onCheckedChange={(checked) => handleInputChange("needs_dishes", checked)}
              />
              <Label htmlFor="needs_dishes" className="text-sm">Geschirr nutzen (+0€)</Label>
            </div> */}
          </div>

          {/* Preisvorschau */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Calculator className="w-4 h-4" style={{ color: 'var(--primary)' }} />
              <span className="font-medium text-sm">Preisvorschau</span>
            </div>
            
            {/* Buchungszeitraum */}
            <div className="bg-white rounded p-2 mb-3 border">
              <div className="text-xs text-gray-700 space-y-1">
                <div className="flex items-center gap-2">
                  <Calendar className="w-3 h-3" style={{ color: 'var(--primary)' }} />
                  <span>
                    <strong>Datum:</strong> {format(selectedDateRange.start, 'dd.MM.yyyy', { locale: de })}
                    {selectedDateRange.start.getTime() !== selectedDateRange.end.getTime() && (
                      <span> - {format(selectedDateRange.end, 'dd.MM.yyyy', { locale: de })}</span>
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-3 h-3" style={{ color: 'var(--primary)' }} />
                  <span>
                    <strong>Zeit:</strong> {formData.start_time} - {formData.end_time} Uhr
                  </span>
                </div>
              </div>
            </div>
            
            {priceLoading && (
              <div className="text-center py-2">
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span className="ml-2 text-sm text-gray-600">Preis wird berechnet...</span>
              </div>
            )}
            
            {priceError && (
              <div className="bg-red-50 border border-red-200 rounded p-2 text-red-700 text-sm">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Fehler beim Berechnen des Preises: {priceError}
              </div>
            )}
            
            {priceCalculation && !priceLoading && (
              <>
                <div className="space-y-1 text-sm text-gray-600 mb-2">
                  {priceCalculation.details.map((detail, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Euro className="w-3 h-3" style={{ color: 'var(--primary)' }} />
                      <span>{detail}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t pt-2">
                  <div className="flex items-center justify-between font-bold text-sm">
                    <span>Gesamtpreis:</span>
                    <span style={{ color: 'var(--primary)' }}>{priceCalculation.totalPrice}€ {priceCalculation.currency}</span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Hinweise zur Abrechnung */}
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="text-xs text-blue-800 space-y-1">
              <p className="font-medium">Wichtige Hinweise:</p>
              <p>• <strong>Buchungsvorlauf:</strong> Anfragen mindestens 3 Tage im Voraus (bei Wochenend-Buchungen muss der Freitag 3 Tage entfernt sein)</p>
              <p>• <strong>Wochentag (Mo-Do):</strong> Buchungen zwischen 8:00-22:00 Uhr, Abrechnung pro angefangene 4 Stunden</p>
              <p>• <strong>Wochenende (Fr-So):</strong> Startzeit 8:00-22:00 Uhr, Endzeit frei wählbar (0:00-23:30), pauschale Abrechnung</p>
              <p>• Mindestbuchungszeit: 1 Stunde</p>
            </div>
          </div>

          {/* Verfügbarkeitshinweis */}
          {availabilityCheck && !availabilityCheck.isAvailable && (
            <div className={`rounded-lg p-3 ${
              availabilityCheck.hasAccepted 
                ? 'bg-red-50 border border-red-300' 
                : 'bg-orange-50 border border-orange-300'
            }`}>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  <p className={`font-medium text-sm ${
                    availabilityCheck.hasAccepted ? 'text-red-900' : 'text-orange-900'
                  }`}>
                    {availabilityCheck.hasAccepted 
                      ? "Zeitraum bereits belegt" 
                      : "Anfrage bereits vorhanden"
                    }
                  </p>
                </div>
                
                <p className={`text-xs ${
                  availabilityCheck.hasAccepted ? 'text-red-800' : 'text-orange-800'
                }`}>
                  {availabilityCheck.hasAccepted 
                    ? "Dieser Zeitraum ist bereits belegt und kann nicht gebucht werden." 
                    : "Für diesen Zeitraum liegt bereits eine Anfrage vor. Sie können trotzdem eine Anfrage stellen (Bearbeitung in Reihenfolge des Eingangs)."
                  }
                </p>
                
                {/* Zeige die spezifischen Konflikte getrennt nach Status */}
                {(availabilityCheck.formattedAcceptedConflicts?.length > 0 || availabilityCheck.formattedRequestedConflicts?.length > 0) && (
                  <div className="mt-2 space-y-2">
                    {/* Bestätigte Buchungen */}
                    {availabilityCheck.formattedAcceptedConflicts && availabilityCheck.formattedAcceptedConflicts.length > 0 && (
                      <div>
                        <p className="text-xs font-medium mb-1 text-red-900">
                          Bestätigte Buchungen:
                        </p>
                        <div className="space-y-1">
                          {availabilityCheck.formattedAcceptedConflicts.map((conflict, index) => (
                            <div key={index} className="text-xs px-2 py-1 rounded bg-red-200 text-red-900 border border-red-300">
                              {conflict}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Angefragte Buchungen */}
                    {availabilityCheck.formattedRequestedConflicts && availabilityCheck.formattedRequestedConflicts.length > 0 && (
                      <div>
                        <p className="text-xs font-medium mb-1 text-orange-900">
                          Angefragte Buchungen:
                        </p>
                        <div className="space-y-1">
                          {availabilityCheck.formattedRequestedConflicts.map((conflict, index) => (
                            <div key={index} className="text-xs px-2 py-1 rounded bg-orange-200 text-orange-900 border border-orange-300">
                              {conflict}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Weiter-Button */}
          <Button 
            type="button" 
            onClick={nextStep}
            disabled={
              !formData.start_time || 
              !formData.end_time || 
              !isDateRangeBookable ||
              (availabilityCheck && !availabilityCheck.isAvailable && availabilityCheck.hasAccepted)
            }
            className="w-full"
            size="sm"
          >
            Weiter zu Schritt 2
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}

      {/* Schritt 2: Persönliche Daten */}
      {currentStep === 2 && (
        <div className="space-y-4">
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-2">Schritt 2: Persönliche Daten</h3>
          </div>

          {/* Vorlauf-Regel Fehler */}
          {(validationErrors.advance_booking || !isDateRangeBookable) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-red-800 text-sm">
                <AlertTriangle className="w-4 h-4" />
                <span>{validationErrors.advance_booking || dateBookableCheck.reason}</span>
              </div>
            </div>
          )}

          {/* Verfügbarkeitshinweis */}
          {availabilityCheck && !availabilityCheck.isAvailable && (
            <div className={`rounded-lg p-3 ${
              availabilityCheck.hasAccepted 
                ? 'bg-red-50 border border-red-300' 
                : 'bg-orange-50 border border-orange-300'
            }`}>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  <p className={`font-medium text-sm ${
                    availabilityCheck.hasAccepted ? 'text-red-900' : 'text-orange-900'
                  }`}>
                    {availabilityCheck.hasAccepted 
                      ? "Zeitraum bereits belegt" 
                      : "Anfrage bereits vorhanden"
                    }
                  </p>
                </div>
                
                <p className={`text-xs ${
                  availabilityCheck.hasAccepted ? 'text-red-800' : 'text-orange-800'
                }`}>
                  {availabilityCheck.hasAccepted 
                    ? "Dieser Zeitraum ist bereits belegt und kann nicht gebucht werden." 
                    : "Für diesen Zeitraum liegt bereits eine Anfrage vor. Sie können trotzdem eine Anfrage stellen (Bearbeitung in Reihenfolge des Eingangs)."
                  }
                </p>
                
                {/* Zeige die spezifischen Konflikte getrennt nach Status */}
                {(availabilityCheck.formattedAcceptedConflicts?.length > 0 || availabilityCheck.formattedRequestedConflicts?.length > 0) && (
                  <div className="mt-2 space-y-2">
                    {/* Bestätigte Buchungen */}
                    {availabilityCheck.formattedAcceptedConflicts && availabilityCheck.formattedAcceptedConflicts.length > 0 && (
                      <div>
                        <p className="text-xs font-medium mb-1 text-red-900">
                          Bestätigte Buchungen:
                        </p>
                        <div className="space-y-1">
                          {availabilityCheck.formattedAcceptedConflicts.map((conflict, index) => (
                            <div key={index} className="text-xs px-2 py-1 rounded bg-red-200 text-red-900 border border-red-300">
                              {conflict}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Angefragte Buchungen */}
                    {availabilityCheck.formattedRequestedConflicts && availabilityCheck.formattedRequestedConflicts.length > 0 && (
                      <div>
                        <p className="text-xs font-medium mb-1 text-orange-900">
                          Angefragte Buchungen:
                        </p>
                        <div className="space-y-1">
                          {availabilityCheck.formattedRequestedConflicts.map((conflict, index) => (
                            <div key={index} className="text-xs px-2 py-1 rounded bg-orange-200 text-orange-900 border border-orange-300">
                              {conflict}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Persönliche Daten */}
          <div className="space-y-3">
            <div>
              <Label htmlFor="customer_name" className="text-sm font-medium">Name *</Label>
              <Input 
                id="customer_name"
                value={formData.customer_name}
                onChange={(e) => handleInputChange("customer_name", e.target.value)}
                required
                className={`h-9 text-sm ${validationErrors.customer_name ? 'border-red-500' : ''}`}
                placeholder="Ihr vollständiger Name"
              />
              {validationErrors.customer_name && (
                <p className="text-red-600 text-xs mt-1">{validationErrors.customer_name}</p>
              )}
            </div>

            <div>
              <Label htmlFor="customer_email" className="text-sm font-medium">E-Mail *</Label>
              <Input 
                id="customer_email"
                type="email"
                value={formData.customer_email}
                onChange={(e) => handleInputChange("customer_email", e.target.value)}
                required
                className={`h-9 text-sm ${validationErrors.customer_email ? 'border-red-500' : ''}`}
                placeholder="ihre.email@beispiel.de"
              />
              {validationErrors.customer_email && (
                <p className="text-red-600 text-xs mt-1">{validationErrors.customer_email}</p>
              )}
            </div>

            <div>
              <Label htmlFor="customer_phone" className="text-sm font-medium">Telefon *</Label>
              <Input 
                id="customer_phone"
                type="tel"
                value={formData.customer_phone}
                onChange={(e) => handleInputChange("customer_phone", e.target.value)}
                required
                className={`h-9 text-sm ${validationErrors.customer_phone ? 'border-red-500' : ''}`}
                placeholder="Ihre Telefonnummer"
              />
              {validationErrors.customer_phone && (
                <p className="text-red-600 text-xs mt-1">{validationErrors.customer_phone}</p>
              )}
            </div>

            <div>
              <Label htmlFor="customer_organization" className="text-sm font-medium">Unternehmen/Verein</Label>
              <Input 
                id="customer_organization"
                value={formData.customer_organization}
                onChange={(e) => handleInputChange("customer_organization", e.target.value)}
                className={`h-9 text-sm ${validationErrors.customer_organization ? 'border-red-500' : ''}`}
                placeholder="Name des Unternehmens oder Vereins (optional)"
              />
              {validationErrors.customer_organization && (
                <p className="text-red-600 text-xs mt-1">{validationErrors.customer_organization}</p>
              )}
            </div>

            <div>
              <Label htmlFor="customer_tax_id" className="text-sm font-medium">Steuernummer</Label>
              <Input 
                id="customer_tax_id"
                value={formData.customer_tax_id}
                onChange={(e) => handleInputChange("customer_tax_id", e.target.value.toUpperCase())}
                className={`h-9 text-sm ${validationErrors.customer_tax_id ? 'border-red-500' : ''}`}
                placeholder="DE123456789 (optional)"
                maxLength={11}
              />
              {validationErrors.customer_tax_id && (
                <p className="text-red-600 text-xs mt-1">{validationErrors.customer_tax_id}</p>
              )}
            </div>

            <div>
              <Label htmlFor="event_purpose" className="text-sm font-medium">Zweck der Veranstaltung *</Label>
              <Input 
                id="event_purpose"
                value={formData.event_purpose}
                onChange={(e) => handleInputChange("event_purpose", e.target.value)}
                placeholder="z.B. Geburtstag, Hochzeit, Tagung"
                required
                className={`h-9 text-sm ${validationErrors.event_purpose ? 'border-red-500' : ''}`}
              />
              {validationErrors.event_purpose && (
                <p className="text-red-600 text-xs mt-1">{validationErrors.event_purpose}</p>
              )}
            </div>

            {/* Adressfelder */}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label htmlFor="customer_street" className="text-sm font-medium">Straße *</Label>
                <Input 
                  id="customer_street"
                  value={formData.customer_street}
                  onChange={(e) => handleInputChange("customer_street", e.target.value)}
                  required
                  className={`h-9 text-sm ${validationErrors.customer_street ? 'border-red-500' : ''}`}
                  placeholder="Musterstraße 123"
                />
                {validationErrors.customer_street && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.customer_street}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="customer_zip" className="text-sm font-medium">PLZ *</Label>
                <Input 
                  id="customer_zip"
                  value={formData.customer_zip}
                  onChange={(e) => handleInputChange("customer_zip", e.target.value)}
                  required
                  className={`h-9 text-sm ${validationErrors.customer_zip ? 'border-red-500' : ''}`}
                  placeholder="12345"
                />
                {validationErrors.customer_zip && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.customer_zip}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="customer_city" className="text-sm font-medium">Ort *</Label>
                <Input 
                  id="customer_city"
                  value={formData.customer_city}
                  onChange={(e) => handleInputChange("customer_city", e.target.value)}
                  required
                  className={`h-9 text-sm ${validationErrors.customer_city ? 'border-red-500' : ''}`}
                  placeholder="Musterstadt"
                />
                {validationErrors.customer_city && (
                  <p className="text-red-600 text-xs mt-1">{validationErrors.customer_city}</p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="notes" className="text-sm font-medium">Zusätzliche Anmerkungen</Label>
              <Textarea 
                id="notes"
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                placeholder="Besondere Wünsche, Anzahl Gäste, etc."
                rows={2}
                className="text-sm"
              />
            </div>
          </div>

          {/* Bestätigung der AGBs */}
          <div className="space-y-2">
            <div className="flex items-start space-x-2">
              <Checkbox 
                id="confirmed_terms"
                checked={formData.confirmed_terms}
                onCheckedChange={(checked) => handleInputChange("confirmed_terms", checked)}
                required
              />
              <Label htmlFor="confirmed_terms" className="text-xs">
                Ich bestätige die <a href="/gebuehrenordnung" target="_blank" rel="noopener noreferrer" className="underline text-primary hover:text-primary/80">Gebührenordnung</a> und bin mit der <a href="/hausordnung" target="_blank" rel="noopener noreferrer" className="underline text-primary hover:text-primary/80">Hausordnung</a> einverstanden. *
              </Label>
            </div>
            {validationErrors.confirmed_terms && (
              <p className="text-red-600 text-xs">{validationErrors.confirmed_terms}</p>
            )}
          </div>

          {/* Vollständige Preisberechnung */}
          {priceCalculation && !priceLoading && (
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Calculator className="w-4 h-4" style={{ color: 'var(--primary)' }} />
                <span className="font-medium text-sm">Detaillierte Preisberechnung</span>
              </div>
                {/* Buchungszeitraum */}
                <div className="bg-white rounded p-2 mb-3 border">
                <div className="text-xs text-gray-700 space-y-1">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3 h-3" style={{ color: 'var(--primary)' }} />
                    <span>
                      <strong>Datum:</strong> {format(selectedDateRange.start, 'dd.MM.yyyy', { locale: de })}
                      {selectedDateRange.start.getTime() !== selectedDateRange.end.getTime() && (
                        <span> - {format(selectedDateRange.end, 'dd.MM.yyyy', { locale: de })}</span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-3 h-3" style={{ color: 'var(--primary)' }} />
                    <span>
                      <strong>Zeit:</strong> {formData.start_time} - {formData.end_time} Uhr
                    </span>
                  </div>
                </div>
              </div>
              <div className="space-y-1 text-xs text-gray-600">
                {priceCalculation.details.map((detail, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Euro className="w-3 h-3" style={{ color: 'var(--primary)' }} />
                    <span>{detail}</span>
                  </div>
                ))}
              </div>
              <div className="border-t mt-2 pt-2">
                <div className="flex items-center justify-between font-bold text-sm">
                  <span>Gesamtpreis:</span>
                  <span style={{ color: 'var(--accent)' }}>{priceCalculation.totalPrice}€ {priceCalculation.currency}</span>
                </div>
              </div>
            </div>
          )}

          {/* Submit-Fehler */}
          {validationErrors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-red-800 text-sm">
                <AlertTriangle className="w-4 h-4" />
                <span>{validationErrors.submit}</span>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={prevStep} size="sm" className="flex-1">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
            
            <Button 
              type="submit" 
              disabled={!formData.confirmed_terms || isSubmitting || !isDateRangeBookable}
              className="flex-1"
              size="sm"
            >
              {isSubmitting ? "Wird gesendet..." : "Buchung abschicken"}
            </Button>
          </div>
        </div>
      )}
    </form>
    </div>
  );
}
